import { useQuery } from "@tanstack/react-query";
import { MapPin } from "lucide-react";
import { useState } from "react";
import { DialogQueryState } from "@/components/shared/DialogQueryState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiGet } from "@/lib/api-client";
import { formatCurrency, formatWholeDollars } from "@/lib/format";
import { policyKeys } from "@/lib/query";
import { cn } from "@/lib/utils";
import { locationsDataSchema } from "@/schemas/policy-dialogs";

interface LocationsClassCodesDialogProps {
  policyNumber: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stateCode?: string;
  stateName?: string;
}

export function LocationsClassCodesDialog({
  policyNumber,
  open,
  onOpenChange,
  stateCode,
  stateName,
}: LocationsClassCodesDialogProps) {
  const [explicitSelection, setExplicitSelection] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [...policyKeys.locations(policyNumber), stateCode ?? "all"] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      if (stateCode) params.set("stateCode", stateCode);
      const qs = params.toString();
      return apiGet(
        `/api/policies/${encodeURIComponent(policyNumber)}/locations${qs ? `?${qs}` : ""}`,
        locationsDataSchema,
      );
    },
    enabled: open,
  });

  const locations = data?.locations ?? [];
  // Use the explicit click selection when it points to a still-visible location;
  // otherwise default to the first one. This auto-resets when the state filter
  // changes the location list.
  const selectedLocationNumber =
    explicitSelection && locations.some((l) => l.locationNumber === explicitSelection)
      ? explicitSelection
      : (locations[0]?.locationNumber ?? null);

  const selectedLocation = locations.find((l) => l.locationNumber === selectedLocationNumber);
  const filteredClassCodes = (data?.classCodes ?? []).filter(
    (cc) => cc.locationNumber === selectedLocationNumber,
  );

  const scopeLabel = stateCode ? (stateName ?? stateCode) : "All States";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>Locations / Class Codes — {scopeLabel}</DialogTitle>
          <DialogDescription>Policy {policyNumber}</DialogDescription>
        </DialogHeader>

        <DialogQueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          onRetry={() => refetch()}
          errorMessage="Failed to load locations"
          isEmpty={!isLoading && !isError && data !== undefined && data.locations.length === 0}
          emptyIcon={MapPin}
          emptyMessage="No locations on record for this scope."
        />

        {data && data.locations.length > 0 && (
          <div className="grid max-h-[65vh] grid-cols-12 gap-4 overflow-hidden">
            {/* ─── LEFT: Locations ─── */}
            <section className="col-span-5 flex min-h-0 flex-col">
              <h3 className="mb-2 font-medium text-primary text-sm">Locations</h3>
              <div className="overflow-y-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/50">
                    <tr className="border-b text-left text-muted-foreground text-xs">
                      <th className="px-2 py-2 font-medium">#</th>
                      <th className="px-2 py-2 font-medium">Name</th>
                      <th className="px-2 py-2 font-medium">Address</th>
                      <th className="px-2 py-2 font-medium">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map((loc) => {
                      const isSelected = loc.locationNumber === selectedLocationNumber;
                      return (
                        <tr
                          key={loc.id}
                          className={cn(
                            "cursor-pointer border-b transition-colors last:border-0",
                            isSelected ? "bg-primary/10" : "hover:bg-muted/50",
                          )}
                          onClick={() => setExplicitSelection(loc.locationNumber)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setExplicitSelection(loc.locationNumber);
                            }
                          }}
                          tabIndex={0}
                          aria-selected={isSelected}
                        >
                          <td className="px-2 py-2 font-medium">{loc.locationNumber}</td>
                          <td className="px-2 py-2">{loc.locationName ?? "—"}</td>
                          <td className="px-2 py-2">
                            <div>{loc.locationAddress1 ?? "—"}</div>
                            {(loc.locationCity || loc.locationZipCode) && (
                              <div className="text-muted-foreground text-xs">
                                {[loc.locationCity, loc.locationZipCode].filter(Boolean).join(" ")}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2">{loc.locationState ?? "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>

            {/* ─── RIGHT: Class Codes ─── */}
            <section className="col-span-7 flex min-h-0 flex-col">
              <div className="mb-2">
                <h3 className="font-medium text-primary text-sm">Class Codes</h3>
                {selectedLocation && (
                  <p className="text-muted-foreground text-xs">
                    Location {selectedLocation.locationNumber}
                    {selectedLocation.locationName ? ` — ${selectedLocation.locationName}` : ""}
                  </p>
                )}
              </div>
              <div className="overflow-y-auto rounded-md border">
                {filteredClassCodes.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-muted/50">
                      <tr className="border-b text-left text-muted-foreground text-xs">
                        <th className="px-2 py-2 font-medium">Class Code</th>
                        <th className="px-2 py-2 font-medium">Description</th>
                        <th className="px-2 py-2 text-right font-medium">Exposure</th>
                        <th className="px-2 py-2 text-right font-medium">Rate</th>
                        <th className="px-2 py-2 text-right font-medium">Manual Premium</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClassCodes.map((cc) => (
                        <tr key={cc.id} className="border-b last:border-0">
                          <td className="px-2 py-2 font-mono text-xs">{cc.classCode}</td>
                          <td className="px-2 py-2">{cc.classDescription ?? "—"}</td>
                          <td className="px-2 py-2 text-right">
                            {formatWholeDollars(cc.exposure)}
                          </td>
                          <td className="px-2 py-2 text-right">{formatCurrency(cc.rate)}</td>
                          <td className="px-2 py-2 text-right">
                            {formatCurrency(cc.manualPremium)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="px-3 py-6 text-center text-muted-foreground text-sm">
                    No class codes for this location.
                  </p>
                )}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
